import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import api from '../../services/api'
import { Table, Column, Badge, RadioButton, RadioGroup } from '../common/Ant'
import { Anchor } from '../common/Anchor'
import { dateTimeFormatter } from '../../utils/date'
import { createQueryString } from '../../utils/queryParams'
import { configurePagination } from '../../utils/helpers'
import {
  ALERT_STATUS,
  DEFAULT_PAGE,
  DEFAULT_PER_PAGE,
  PRESSURE_MONITOR_ALERT_TYPE_LABELS,
  SYSTEM_ALERT_TYPES,
} from '../../constants'

// alerts store the device's location type as their asset type
const PRESSURE_MONITOR_ASSET_TYPE = 'PRESSURE_MONITOR'

const alertName = ({ thresholdName, systemAlertType }) => {
  if (thresholdName) return thresholdName
  if (systemAlertType === SYSTEM_ALERT_TYPES.OFFLINE) {
    return 'Pressure Monitor Offline'
  }
  return PRESSURE_MONITOR_ALERT_TYPE_LABELS[systemAlertType] || 'Alert'
}

const PressureMonitorAlerts = ({ pressureMonitorId }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [alerts, setAlerts] = useState([])
  const [meta, setMeta] = useState({})
  const [page, setPage] = useState(DEFAULT_PAGE)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)
  // undefined shows active and closed alerts
  const [status, setStatus] = useState(undefined)

  const getAlerts = useCallback(() => {
    setIsLoading(true)

    const qs = createQueryString({
      assetType: PRESSURE_MONITOR_ASSET_TYPE,
      assetId: pressureMonitorId,
      status,
      page,
      perPage,
    })

    api
      .getAllAlerts(qs)
      .then(data => {
        setAlerts(data.items || [])
        setMeta(data.meta || {})
        setIsLoading(false)
      })
      .catch(e => {
        console.log('Error getting pressure monitor alerts')
        console.log(e)
        setIsLoading(false)
      })
  }, [pressureMonitorId, status, page, perPage])

  useEffect(() => {
    getAlerts()
  }, [getAlerts])

  const handleStatusChange = e => {
    setStatus(e.target.value || undefined)
    setPage(DEFAULT_PAGE)
  }

  const handleTableChange = pagination => {
    setPage(pagination.current)
    setPerPage(pagination.pageSize)
  }

  return (
    <div>
      <div className="mb3">
        <RadioGroup onChange={handleStatusChange} value={status || ''}>
          <RadioButton value="">All</RadioButton>
          <RadioButton value={ALERT_STATUS.ACTIVE}>
            Active ({meta.activeAlertCount || 0})
          </RadioButton>
          <RadioButton value={ALERT_STATUS.INACTIVE}>
            Closed ({meta.inactiveAlertCount || 0})
          </RadioButton>
        </RadioGroup>
      </div>
      <Table
        className="nested-table"
        dataSource={alerts}
        rowKey="id"
        loading={isLoading}
        onChange={handleTableChange}
        pagination={configurePagination({
          perPage,
          page,
          total: meta.total,
          onShowSizeChange: (current, size) => setPerPage(size),
        })}
      >
        <Column
          title="Alert"
          dataIndex="thresholdName"
          render={(text, record) => {
            const anchor = (
              <Anchor to={`/alerts/pods/${record.slug}`} className="pl2">
                {alertName(record)}
              </Anchor>
            )

            return record.alertActive ? (
              <span className="flex items-center">
                <Badge status="error" /> <span className="b">{anchor}</span>
              </span>
            ) : (
              <span className="flex items-center">
                <Badge status="default" /> {anchor}
              </span>
            )
          }}
        />
        <Column
          title="Start"
          dataIndex="startTime"
          render={text => (text ? dateTimeFormatter(text) : '--')}
        />
        <Column
          title="End"
          dataIndex="endTime"
          render={text => (text ? dateTimeFormatter(text) : '--')}
        />
        <Column
          title="Site"
          dataIndex="siteName"
          render={(text, record) =>
            text ? (
              <Anchor to={`/sites/${record.siteSlug}`}>{text}</Anchor>
            ) : (
              '--'
            )
          }
        />
        <Column
          title="Location"
          dataIndex="locationName"
          render={(text, record) =>
            text ? (
              <Anchor
                to={`/sites/${record.siteSlug}/floors/${record.floorId}/locations/${record.locationId}`}
              >
                {text}
              </Anchor>
            ) : (
              '--'
            )
          }
        />
      </Table>
    </div>
  )
}

PressureMonitorAlerts.propTypes = {
  pressureMonitorId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
}

export default PressureMonitorAlerts
